import React, { useState, useRef, useEffect, useCallback } from 'react';

const SafeImage = ({ 
  src, 
  alt = "Image", 
  className = "w-full h-full object-cover", 
  fallbackSrc = "https://placehold.co/400x300",
  lazy = true,
  onClick,
  ...props
}) => {
  // Check if the S3 URL has been modified with query parameters or is expired
  const isUrlModified = useCallback((url) => {
    if (!url || typeof url !== 'string') return false;
    
    // Handle data URLs (base64 encoded images) - these are safe
    if (url.startsWith('data:image/')) {
      return false;
    }
    
    // Check if this is an upload URL (contains x-id=PutObject) - these are always invalid for display
    if (url.includes('x-id=PutObject')) {
      return true;
    }
    
    // Check if it's an S3 URL with query parameters that break the signature
    if (url.includes('s3.amazonaws.com') && url.includes('?')) {
      // Check for common image optimization parameters - these ALWAYS break S3 signatures
      const hasOptimizationParams = url.includes('w=') || url.includes('h=') || url.includes('fit=') || url.includes('auto=');
      if (hasOptimizationParams) {
        return true;
      }
      
      // Check if the URL might be expired (X-Amz-Expires parameter)
      if (url.includes('X-Amz-Expires')) {
        const expiresMatch = url.match(/X-Amz-Expires=(\d+)/);
        if (expiresMatch) {
          const expiresIn = parseInt(expiresMatch[1]);
          if (expiresIn < 3600) { // Less than 1 hour remaining
            return true;
          }
        }
      }
    }
    
    return false;
  }, []);

  const [imageSrc, setImageSrc] = useState(() => {
    // Don't set corrupted URLs initially
    if (lazy) return null;
    if (src && isUrlModified(src)) return fallbackSrc;
    return src;
  });
  const [isLoading, setIsLoading] = useState(lazy);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (!lazy || !src) {
      // Check if the URL is safe before setting it
      if (src && isUrlModified(src)) {
        setImageSrc(fallbackSrc);
        setHasError(true);
      } else {
        setImageSrc(src);
      }
      setIsLoading(false);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !imageSrc) {
            // Check if the URL is safe before setting it
            if (src && isUrlModified(src)) {
              setImageSrc(fallbackSrc);
              setHasError(true);
            } else {
              setImageSrc(src);
            }
            setIsLoading(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
      observerRef.current = observer;
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [src, lazy, imageSrc, fallbackSrc, isUrlModified]);

  // Ensure we never render corrupted URLs
  const safeImageSrc = hasError ? fallbackSrc : imageSrc;

  // Effect to handle src changes and ensure we always have a safe URL
  useEffect(() => {
    if (src) {
      // Immediately check if the URL is corrupted
      if (isUrlModified(src)) {
        setImageSrc(fallbackSrc);
        setHasError(true);
        return;
      }
      
      // Only set the URL if it's safe
      if (src !== imageSrc) {
        setImageSrc(src);
        setHasError(false);
      }
    }
  }, [src, fallbackSrc, isUrlModified, imageSrc]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
    setImageSrc(fallbackSrc);
  };

  // If lazy loading and no src set yet, show placeholder
  if (lazy && !imageSrc) {
    return (
      <div 
        ref={imgRef}
        className={`${className} bg-gray-200 flex items-center justify-center`}
        onClick={onClick}
        {...props}
      >
        {isLoading ? (
          <div className="animate-pulse bg-gray-300 w-full h-full rounded"></div>
        ) : (
          <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
        )}
      </div>
    );
  }

  // If the incoming src is corrupted, show placeholder immediately
  if (src && isUrlModified(src)) {
    return (
      <div 
        className={`${className} bg-gray-200 flex items-center justify-center`}
        onClick={onClick}
        {...props}
      >
        <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }

  // If we have an error or corrupted imageSrc, show placeholder
  if (hasError || (imageSrc && isUrlModified(imageSrc))) {
    return (
      <div 
        className={`${className} bg-gray-200 flex items-center justify-center`}
        onClick={onClick}
        {...props}
      >
        <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {isLoading && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse rounded"></div>
      )}
      
      <img
        ref={imgRef}
        src={safeImageSrc}
        alt={alt}
        className={`${className} transition-opacity duration-200 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        onLoad={handleImageLoad}
        onError={handleImageError}
        onClick={onClick}
        loading={lazy ? "lazy" : "eager"}
        decoding="async"
        {...props}
      />
    </div>
  );
};

export default SafeImage;
